import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import { usePayoutBatchEvents } from "../../hooks/usePayoutBatchEvents";
import { BatchTimeline } from "./BatchTimeline";

export interface BatchEventsDrawerProps {
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bumps when the batch moved; the open drawer re-reads its first page. */
  revision: number;
  isLive: boolean;
}

/** The audit trail of the batch, read on demand and kept current while open. */
export function BatchEventsDrawer({ batchId, open, onOpenChange, revision, isLive }: BatchEventsDrawerProps) {
  const { t } = useTranslations("payoutBatches");
  const { user } = useAuth();
  const events = usePayoutBatchEvents(batchId, open);

  const seenRevision = useRef(revision);
  const refreshRef = useRef(events.refresh);
  useEffect(() => {
    refreshRef.current = events.refresh;
  });
  useEffect(() => {
    if (!open || seenRevision.current === revision) return;
    seenRevision.current = revision;
    void refreshRef.current();
  }, [open, revision]);

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={t("events_title")}
      description={t("events_description")}
      size="md"
      pinLastChild={false}
    >
      <div className="space-y-4">
        {events.hasError ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={t("events_error")}
            actions={
              <ColocaButton variant="default" size="xs" onClick={() => void events.refresh()}>
                {t("error_retry")}
              </ColocaButton>
            }
          />
        ) : (
          <BatchTimeline
            events={events.events}
            currentUserId={user?.id ?? null}
            isLoading={events.isLoading}
            isLive={isLive}
          />
        )}
        {events.hasMore && !events.hasError && (
          <ColocaButton variant="ghost" size="sm" onClick={() => void events.loadMore()}>
            {t("events_load_more")}
          </ColocaButton>
        )}
      </div>
    </ColocaDrawer>
  );
}
